/**
 * RepConnect - Healthcare Professional Dashboard
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import {
  Calendar, Clock, Users, Coffee, DollarSign, ChevronRight,
  Search, AlertCircle, CreditCard,
  CheckCircle, X, User, Bell, MapPin, Utensils, Send, Building, Settings,
  GripVertical, Eye, EyeOff, RotateCcw, CheckSquare, Square, UserCheck
} from 'lucide-react';
import { meetingService } from '../utils/supabaseData';
import { MeetingStorage } from '../utils/meetingStorage';
import { UserStorage } from '../utils/userStorage';
import { PaymentUtils } from '../utils/stripeConfig';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import StatCard from './StatCard';
import MobileLoadingSpinner from './MobileLoadingSpinner';
import MeetingTrendChart from './MeetingTrendChart';
import MeetingInsights from './MeetingInsights';
import EnhancedMeetingCard from './EnhancedMeetingCard';
import CombinedMeetingStatus from './CombinedMeetingStatus';
import Tooltip, { HelpTooltip, InfoTooltip } from './Tooltip';
import HelpBanner from './HelpBanner';
import GuidedTour from './GuidedTour';
import ContextualHelp from '../utils/contextualHelp';
import { logger } from '../utils/logger';
// import { ExternalProviderInvitation } from '../utils/externalProviderInvitation';

const Dashboard = ({ userType, profileData, setCurrentView, setSelectedMeetingId, setSelectedMeetingDate, setShowRequestCreator, delegatingFor, isDelegated, hasValidSubscription: passedSubscriptionStatus, currentUser: passedCurrentUser, effectiveUser: passedEffectiveUser, updateNotificationCount }) => {
  const [meetings, setMeetings] = useState([]);
  const [meetingStats, setMeetingStats] = useState(null);
  const [currentUser, setCurrentUser] = useState(passedCurrentUser || null);
  const [effectiveUser, setEffectiveUser] = useState(passedEffectiveUser || passedCurrentUser || null);
  const [hasValidSubscription, setHasValidSubscription] = useState(passedSubscriptionStatus || false);
  const [preApprovalCount, setPreApprovalCount] = useState(0);

  // Update state when props change
  useEffect(() => {
    if (passedCurrentUser) {
      setCurrentUser(passedCurrentUser);
    }
    if (passedEffectiveUser) {
      setEffectiveUser(passedEffectiveUser);
    } else if (passedCurrentUser) {
      setEffectiveUser(passedCurrentUser);
    }
    if (passedSubscriptionStatus !== undefined) {
      setHasValidSubscription(passedSubscriptionStatus);
    }
  }, [passedCurrentUser, passedEffectiveUser, passedSubscriptionStatus]);
  const [loading, setLoading] = useState(true);
  const [meetingRequests, setMeetingRequests] = useState([]);
  const [approvedReps, setApprovedReps] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [actionStatus, setActionStatus] = useState('');
  // Removed unused external invitation state - keeping for future features
  // const [externalInvitations, setExternalInvitations] = useState([]);
  // const [invitationStats, setInvitationStats] = useState([]);
  
  // Dashboard customization state
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [dashboardLayout, setDashboardLayout] = useState({
    sections: ['quickActions', 'upcomingMeetings', 'charts', 'stats', 'comprehensiveUpcoming'],
    hiddenSections: [],
    statOrder: ['upcomingMeetings', 'thisMonth', 'totalMeetings']
  });
  const [draggedItem, setDraggedItem] = useState(null);

  // Bulk actions state
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Contextual help state
  const [showTour, setShowTour] = useState(false);
  const [helpContext, setHelpContext] = useState(null);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);

  // Simple click-to-reorder system
  const handleSectionClick = (sectionId) => {
    if (!isCustomizing) return;
    
    if (!draggedItem) {
      // First click - select the section to move
      setDraggedItem({ item: sectionId, type: 'section' });

    } else if (draggedItem.item === sectionId) {
      // Click same section - deselect
      setDraggedItem(null);

    } else {
      // Second click - move the selected section here

      const newSections = [...dashboardLayout.sections];
      const dragIndex = newSections.indexOf(draggedItem.item);
      const dropIndex = newSections.indexOf(sectionId);
      
      if (dragIndex !== -1 && dropIndex !== -1) {
        newSections.splice(dragIndex, 1);
        newSections.splice(dropIndex, 0, draggedItem.item);
        
        setDashboardLayout(prev => ({ ...prev, sections: newSections }));

        alert(`Moved ${draggedItem.item} to ${sectionId} position!`);
      }
      
      setDraggedItem(null);
    }
  };

  // These are now inline in the JSX for better debugging

  const handleDrop = (e, targetItem, targetType) => {
    e.preventDefault();
    e.stopPropagation();

    // Clean up visual feedback
    e.currentTarget.style.backgroundColor = '';
    e.currentTarget.style.borderColor = '';
    
    if (!draggedItem) {

      return;
    }
    
    if (!isCustomizing) {

      return;
    }

    if (draggedItem.item === targetItem) {

      setDraggedItem(null);
      return;
    }

    if (draggedItem.type === 'section' && targetType === 'section') {

      // Reorder sections
      const newSections = [...dashboardLayout.sections];
      const dragIndex = newSections.indexOf(draggedItem.item);
      const dropIndex = newSections.indexOf(targetItem);

      if (dragIndex !== -1 && dropIndex !== -1) {
        newSections.splice(dragIndex, 1);
        newSections.splice(dropIndex, 0, draggedItem.item);

        setDashboardLayout(prev => ({ ...prev, sections: newSections }));
        alert('Sections reordered! Check the dashboard.');
      } else {

      }
    }
    
    setDraggedItem(null);
  };

  const toggleSectionVisibility = (sectionId) => {
    setDashboardLayout(prev => ({
      ...prev,
      hiddenSections: prev.hiddenSections.includes(sectionId)
        ? prev.hiddenSections.filter(id => id !== sectionId)
        : [...prev.hiddenSections, sectionId]
    }));
  };

  const resetLayout = () => {
    setDashboardLayout({
      sections: ['quickActions', 'charts', 'stats', 'comprehensiveUpcoming', 'meetingRequests', 'upcomingMeetings'],
      hiddenSections: [],
      statOrder: ['meetingRequests', 'upcomingMeetings', 'thisMonth', 'totalMeetings']
    });
  };

  // Clickable Section Component
  const DraggableSection = ({ sectionId, title, children, className = "" }) => {
    const isHidden = dashboardLayout.hiddenSections.includes(sectionId);
    const isSelected = draggedItem?.item === sectionId && draggedItem?.type === 'section';
    
    if (isHidden && !isCustomizing) return null;
    
    return (
      <div
        className={`transition-all duration-200 border-2 ${
          isSelected 
            ? 'border-blue-500 bg-blue-50 shadow-lg' 
            : isCustomizing 
              ? 'border-dashed border-gray-300 hover:border-blue-400 cursor-pointer' 
              : 'border-transparent'
        } ${className} ${isCustomizing ? 'min-h-[100px]' : ''}`}
        onClick={() => handleSectionClick(sectionId)}
      >
        {isCustomizing && (
          <div className="flex items-center justify-between mb-2 p-2 bg-gray-100 rounded-lg border border-gray-300">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 text-blue-500">
                {isSelected ? '✅' : '👆'}
              </div>
              <span className="text-sm font-medium text-gray-700">
                {isSelected ? `Selected: ${title}` : `Click to select: ${title}`}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSectionVisibility(sectionId);
              }}
              className="text-gray-500 hover:text-gray-700 p-1 cursor-pointer"
              title={isHidden ? 'Show section' : 'Hide section'}
            >
              {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        )}
        <div className={`${isHidden ? 'opacity-50 pointer-events-none' : ''} ${isCustomizing ? 'relative' : ''}`}>
          {isCustomizing && (
            <div className="absolute top-0 left-0 right-0 bottom-0 border-2 border-dashed border-gray-400 rounded-lg pointer-events-none opacity-30">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-200 px-2 py-1 rounded text-xs text-gray-600">
                Drop zone for {title}
              </div>
            </div>
          )}
          {children}
        </div>
      </div>
    );
  };

  // Render sections in custom order
  const renderSections = () => {
    const sectionComponents = {
      stats: (
        <DraggableSection sectionId="stats" title="Meeting Status Overview" className="mb-6 md:mb-8">
          <div className="grid gap-4 md:gap-6 lg:grid-cols-2" data-tour="stat-cards">
            {/* Combined Meeting Status - Primary focus */}
            <CombinedMeetingStatus meetings={meetings} userType={userType} />

            {/* Additional stats for sales reps */}
            {userType === 'sales' && (
              <div className="space-y-4">
                <StatCard
                  icon={<DollarSign className="w-6 h-6 text-green-600" />}
                  title="Total Spending"
                  value={`$${meetingStats?.totalSpent?.toLocaleString() || '0'}`}
                  change="This month"
                  trend="up"
                />
                <StatCard
                  icon={<Users className="w-6 h-6 text-purple-600" />}
                  title="Customers Met"
                  value={meetingStats?.uniqueProviders || 0}
                  change="Unique customers"
                  trend="up"
                />
                <StatCard
                  icon={<UserCheck className="w-6 h-6 text-blue-600" />}
                  title="Pre-Approved By"
                  value={preApprovalCount}
                  change="Customers"
                  trend="up"
                />
              </div>
            )}

            {/* Additional stats for healthcare providers - This Month now in Meeting Insights */}
          </div>
        </DraggableSection>
      ),

      charts: (
        <DraggableSection sectionId="charts" title="Analytics & Insights" className="mb-6 md:mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <MeetingTrendChart
              meetings={meetings}
              userType={userType}
              title={userType === 'sales' ? 'Meeting Activity' : 'Meeting Schedule'}
              onDayClick={(date, dayMeetings) => {
                if (dayMeetings && dayMeetings.length === 1) {
                  // If there's exactly one meeting on this day, go directly to meeting detail
                  const meeting = dayMeetings[0];
                  setSelectedMeetingId(meeting.id);
                  setCurrentView('meeting-detail');
                } else {
                  // If there are multiple meetings or no meetings, go to daily view for this date
                  if (setSelectedMeetingDate) {
                    setSelectedMeetingDate(date);
                  }
                  // Use 'requests' view for customers, 'meetings' for sales reps
                  setCurrentView(userType === 'customer' ? 'requests' : 'meetings');
                }
              }}
            />
            <MeetingInsights
              meetings={meetings}
              userType={userType}
              meetingStats={meetingStats}
              meetingsThisWeekCount={meetingsThisWeekCount}
            />
          </div>
        </DraggableSection>
      ),

      upcomingMeetings: filteredUpcomingMeetings.length > 0 ? (
        <DraggableSection sectionId="upcomingMeetings" title="Your Next Three" className="mb-6 md:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">
                Your Next Three
              </h3>
              {filteredUpcomingMeetings.length > 3 && (
                <button onClick={() => setCurrentView('meetings')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  View All Meetings
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 max-w-md md:max-w-none mx-auto md:mx-0">
              {filteredUpcomingMeetings.slice(0, 3).map(meeting => (
                <EnhancedMeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onClick={() => handleMeetingClick(meeting)}
                  userType={userType}
                />
              ))}
            </div>
          </div>
        </DraggableSection>
      ) : null,

      comprehensiveUpcoming: userType === 'customer' && comprehensiveUpcomingMeetings.length > 0 ? (
        <DraggableSection sectionId="comprehensiveUpcoming" title="Future Meetings" className="mb-6 md:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                Future Meetings
                <span className="ml-2 text-sm text-gray-500">(Beyond This Week)</span>
              </h3>
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  {comprehensiveUpcomingMeetings.length} scheduled
                </span>
                {comprehensiveUpcomingMeetings.length > 6 && (
                  <button
                    onClick={() => setCurrentView('meetings')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View All →
                  </button>
                )}
              </div>
            </div>

            {/* Group meetings by month for better organization */}
            <div className="space-y-6">
              {(() => {
                const meetingsByMonth = comprehensiveUpcomingMeetings
                  .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date))
                  .reduce((acc, meeting) => {
                    const date = parseLocalDate(meeting.date);
                    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                    const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

                    if (!acc[monthKey]) {
                      acc[monthKey] = { name: monthName, meetings: [] };
                    }
                    acc[monthKey].meetings.push(meeting);
                    return acc;
                  }, {});

                return Object.entries(meetingsByMonth).slice(0, 3).map(([monthKey, { name, meetings }]) => (
                  <div key={monthKey}>
                    <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                      {name}
                      <span className="ml-2 text-sm text-gray-500">({meetings.length} meeting{meetings.length !== 1 ? 's' : ''})</span>
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                      {meetings.slice(0, 6).map(meeting => (
                        <EnhancedMeetingCard
                          key={meeting.id}
                          meeting={meeting}
                          onClick={() => handleMeetingClick(meeting)}
                          userType={userType}
                          compact={true}
                        />
                      ))}
                    </div>
                    {meetings.length > 6 && (
                      <div className="mt-3 text-center">
                        <button
                          onClick={() => setCurrentView('meetings')}
                          className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          + {meetings.length - 6} more meetings in {name}
                        </button>
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>

            {/* Show summary if there are more months */}
            {(() => {
              const totalMonths = Object.keys(comprehensiveUpcomingMeetings
                .reduce((acc, meeting) => {
                  const date = parseLocalDate(meeting.date);
                  const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                  acc[monthKey] = true;
                  return acc;
                }, {})).length;

              if (totalMonths > 3) {
                return (
                  <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg text-center">
                    <p className="text-purple-800 text-sm">
                      <strong>{totalMonths - 3} more months</strong> with scheduled meetings.
                    </p>
                    <button
                      onClick={() => setCurrentView('meetings')}
                      className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      View Complete Schedule
                    </button>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </DraggableSection>
      ) : null,

      quickActions: (
        <DraggableSection sectionId="quickActions" title="Quick Actions" className="mb-6 md:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6" data-tour="quick-actions">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {userType === 'sales' ? (
                <>
                  <button
                    onClick={() => setCurrentView('find')}
                    className="w-full text-left p-3 bg-blue-50 rounded-lg hover:bg-blue-100 flex items-center justify-between transition-colors border border-blue-200"
                  >
                    <span className="flex items-center">
                      <Send className="w-5 h-5 mr-3 text-blue-600" />
                      <span className="text-blue-800 font-medium">Create meeting request</span>
                    </span>
                    <ChevronRight className="w-5 h-5 text-blue-400" />
                  </button>
                  {/* Other sales actions... */}
                </>
              ) : (
                <div className="space-y-6">
                  {/* Meeting-related Actions */}
                  <div>
                    <div className="flex items-center mb-3">
                      <Bell className="w-4 h-4 text-blue-600 mr-2" />
                      <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Meeting Management</h4>
                    </div>
                    <button
                      onClick={() => setCurrentView('meetings')}
                      className="w-full text-left p-4 md:p-4 bg-blue-50 rounded-lg hover:bg-blue-100 active:bg-blue-200 flex items-center justify-between transition-colors border border-blue-200 touch-manipulation min-h-[60px]"
                    >
                      <span className="flex items-center">
                        <div className="relative mr-3">
                          <Bell className="w-5 h-5 text-blue-600" />
                          {meetingRequests.length > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                              {meetingRequests.length > 9 ? '9+' : meetingRequests.length}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-blue-800 font-medium">Review meeting requests</span>
                          {meetingRequests.length > 0 && (
                            <span className="text-blue-600 text-sm">
                              {meetingRequests.length} pending request{meetingRequests.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </span>
                      <ChevronRight className="w-5 h-5 text-blue-400" />
                    </button>
                  </div>

                  {/* Profile-related Actions */}
                  <div>
                    <div className="flex items-center mb-3">
                      <Settings className="w-4 h-4 text-gray-600 mr-2" />
                      <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Profile Settings</h4>
                    </div>
                    <div className="space-y-2">
                      <button
                        onClick={() => setCurrentView('availability')}
                        className="w-full text-left p-3 md:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 active:bg-gray-200 flex items-center justify-between transition-colors border border-gray-200 touch-manipulation min-h-[52px]"
                      >
                        <span className="flex items-center text-gray-800">
                          <Clock className="w-5 h-5 mr-3 text-gray-600" />
                          <span className="font-medium">Update availability</span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DraggableSection>
      )
    };

    return dashboardLayout.sections.map(sectionId => sectionComponents[sectionId]).filter(Boolean);
  };

  // Load user meetings and stats
  useEffect(() => {
    const loadData = async () => {
      if (effectiveUser) {
        // Clear existing meetings first to force fresh load
        setMeetings([]);

        // Normalize userType field (handle both userType and user_type)
        const actualUserType = effectiveUser.userType || effectiveUser.user_type;

        // Safety check - if no userType, don't proceed
        if (!actualUserType && !userType) {
          logger.error('Dashboard: No userType found on effectiveUser', { userId: effectiveUser?.id });
          return;
        }

        // For office staff, App.js passes effectiveUser as the physician's profile (via getEffectiveUser)
        // So effectiveUser.id is already the physician's ID - just use it directly
        const effectiveUserId = effectiveUser.id;

        // Office staff sees customer dashboard
        const effectiveUserType = (isDelegated || actualUserType === 'office_staff') ? 'customer' : (actualUserType || userType);

        console.log('🔍 DASHBOARD DEBUG:', {
          effectiveUserId,
          effectiveUserType,
          isDelegated,
          actualUserType,
          delegatingForName: delegatingFor?.name,
          delegatingForId: delegatingFor?.id
        });

        logger.debug('Dashboard loading data:', {
          staffId: effectiveUser.id,
          effectiveUserId,
          effectiveUserType,
          isDelegated,
          delegatingFor: delegatingFor?.name
        });

        // Try Supabase first, fallback to localStorage
        try {
          const supabaseMeetings = await meetingService.getAllMeetings(effectiveUserId, effectiveUserType);

          if (!supabaseMeetings || supabaseMeetings.length === 0) {
            logger.warn('Dashboard: No meetings returned from Supabase, checking if this should fallback to localStorage');
            throw new Error('No meetings from Supabase');
          }

          // Transform Supabase data to match Dashboard expectations (same as MeetingsView.js)
          const transformedMeetings = await Promise.all(supabaseMeetings.map(async meeting => {
            let customerName = userType === 'customer' ? 'Sales Representative' : 'Customer';
            let customerProfilePicture = null;
            let debugInfo = { meetingId: meeting.id, customerId: meeting.customer_id, salesRepId: meeting.sales_rep_id };

            // Fetch customer name if available - Enhanced error handling

            if (meeting.customer_id && userType === 'sales') {
              try {

                const { supabase } = await import('../utils/supabase');
                const { data: customerData, error } = await supabase
                  .from('user_profiles')
                  .select('first_name, last_name, email, id, profile_picture')
                  .eq('id', meeting.customer_id)
                  .single();

                debugInfo.customerQuery = { error, data: customerData };

                if (error) {
                  logger.warn('Dashboard: Customer query error', { error: error.message, customerId: meeting.customer_id });
                } else if (customerData) {

                  if (customerData.first_name || customerData.last_name) {
                    customerName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim();

                  } else if (customerData.email) {
                    customerName = customerData.email.split('@')[0]; // Use email prefix as fallback

                  } else {
                    logger.warn('Dashboard: Customer has no name or email data', { customerId: meeting.customer_id });
                  }
                  customerProfilePicture = customerData.profile_picture;
                } else {
                  logger.warn('Dashboard: No customer data returned for ID', { customerId: meeting.customer_id });
                }
              } catch (fetchError) {
                logger.warn('Dashboard: Customer fetch exception', { error: fetchError.message, meetingId: meeting.id });
                debugInfo.fetchError = fetchError.message;
              }
            } else if (meeting.customer_id) {

            }

            // For healthcare providers viewing their own meetings, fetch sales rep name directly
            let salesRepProfilePicture = null;
            if (effectiveUser.userType === 'customer' || userType === 'customer') {
              // For customers, show the sales rep name (who they're meeting with)
              if (meeting.sales_rep_id) {
                try {
                  const { supabase } = await import('../utils/supabase');
                  const { data: salesRepData, error } = await supabase
                    .from('user_profiles')
                    .select('first_name, last_name, email, profile_picture')
                    .eq('id', meeting.sales_rep_id)
                    .single();

                  if (error) {
                    logger.warn('Dashboard: Sales rep query error', { error: error.message, salesRepId: meeting.sales_rep_id });
                    customerName = 'Sales Representative';
                  } else if (salesRepData) {
                    if (salesRepData.first_name || salesRepData.last_name) {
                      customerName = `${salesRepData.first_name || ''} ${salesRepData.last_name || ''}`.trim();
                    } else if (salesRepData.email) {
                      customerName = salesRepData.email.split('@')[0];
                    } else {
                      customerName = 'Sales Representative';
                    }
                    salesRepProfilePicture = salesRepData.profile_picture;
                  } else {
                    customerName = 'Sales Representative';
                  }
                } catch (fetchError) {
                  logger.warn('Dashboard: Sales rep fetch exception', { error: fetchError.message });
                  customerName = 'Sales Representative';
                }
              } else if (meeting.external_sales_rep_name) {
                // External sales rep (manually added meeting)
                customerName = meeting.external_sales_rep_name;
                logger.debug('Dashboard: Using external sales rep name', { name: customerName, meetingId: meeting.id });
              } else {
                logger.warn('Dashboard: No sales_rep_id or external rep found for customer meeting', { meetingId: meeting.id });
                customerName = 'Sales Representative';
              }
            }

            const transformedMeeting = {
              // Keep original Supabase fields
              ...meeting,
              // Transform to match Dashboard expectations
              date: meeting.meeting_date,
              time: meeting.start_time,
              salesRepId: meeting.sales_rep_id,
              customerId: meeting.customer_id,
              customer: customerName,
              salesRep: userType === 'customer' ? customerName : 'Customer',
              customer_profile_picture: customerProfilePicture,
              sales_rep_profile_picture: salesRepProfilePicture,
              restaurant: meeting.restaurant_name || meeting.restaurant || 'TBD',
              status: meeting.status || 'pending',
              orderTotal: meeting.actual_cost || meeting.estimated_cost || '0.00',
              notes: meeting.meeting_notes || meeting.notes || '',
              _debugInfo: debugInfo
            };

            logger.debug(`🔍 Dashboard: Final transformed meeting ${meeting.id}:`, {
              id: transformedMeeting.id,
              customer: transformedMeeting.customer,
              date: transformedMeeting.date,
              status: transformedMeeting.status
            });

            return transformedMeeting;
          }));

          logger.debug('🔍 Dashboard: Meeting dates found:', transformedMeetings.map(m => ({
            id: m.id,
            date: m.date,
            customer: m.customer,
            status: m.status
          })));
          setMeetings(transformedMeetings);

          // Calculate stats from transformed data
          const completedMeetings = transformedMeetings.filter(m => m.status === 'completed');

          // Count unique customers from ALL meetings (not just completed)
          // This includes approved, confirmed, pending, and completed meetings
          const uniqueCustomerIds = new Set(
            transformedMeetings
              .filter(m => m.customer_id && m.status !== 'cancelled' && m.status !== 'declined')
              .map(m => m.customer_id)
          );

          // Calculate total spending for current month (exclude cancelled meetings)
          const now = new Date();
          const currentMonth = now.getMonth();
          const currentYear = now.getFullYear();

          const meetingsThisMonth = transformedMeetings.filter(m => {
            if (!m.date || m.status === 'cancelled') return false;
            const meetingDate = new Date(m.date);
            return meetingDate.getMonth() === currentMonth && meetingDate.getFullYear() === currentYear;
          });

          const totalSpentThisMonth = meetingsThisMonth.reduce((sum, m) => sum + parseFloat(m.orderTotal || 0), 0);

          const stats = {
            total: transformedMeetings.length,
            pending: transformedMeetings.filter(m => m.status === 'pending').length,
            upcoming: transformedMeetings.filter(m => {
              const meetingDate = new Date(m.date);
              return meetingDate >= new Date() && (m.status === 'approved' || m.status === 'pending');
            }).length,
            completed: completedMeetings.length,
            uniqueProviders: uniqueCustomerIds.size,
            totalSpent: totalSpentThisMonth
          };

          setMeetingStats(stats);

        } catch (error) {
          logger.error('Error loading meetings from Supabase:', { error: error.message });

          const userMeetings = MeetingStorage.getMeetingsForUser(currentUser.id, userType);
          setMeetings(userMeetings);
          const stats = MeetingStorage.getMeetingStats(currentUser.id, userType);
          setMeetingStats(stats);
        }
        // Note: Subscription status is now passed from parent component

        // Load meeting requests for healthcare professionals from Supabase
        if (effectiveUserType === 'customer') {
          try {
            // Get pending meetings from Supabase
            const allMeetings = await meetingService.getAllMeetings(effectiveUserId, 'customer');
            const pendingRequests = allMeetings.filter(meeting => meeting.status === 'pending');

            const enrichedRequests = pendingRequests.map(request => ({
              ...request,
              requestId: request.id,
              salesRepName: request.sales_rep_name || request.salesRepName || request.salesRep || 'Unknown Sales Rep',
              salesRepEmail: request.sales_rep_email || request.salesRepEmail || '',
              salesRepCompany: request.sales_rep_company || request.salesRepCompany || 'Company Not Specified',
              products: request.sales_rep_products || request.products || ['Product information not provided'],
              isUrgent: isRequestUrgent(request)
            }));
            setMeetingRequests(enrichedRequests);

            logger.debug('Dashboard: Loaded pending meeting requests from Supabase', {
              count: enrichedRequests.length,
              requests: enrichedRequests
            });
          } catch (error) {
            logger.warn('Dashboard: Failed to load from Supabase, using localStorage fallback', error);
            // Fallback to localStorage
            const requests = MeetingStorage.getMeetingRequestsForProvider(currentUser.id);
            const enrichedRequests = requests.map(request => ({
              ...request,
              requestId: request.id,
              salesRepName: request.salesRepName || request.salesRep || 'Unknown Sales Rep',
              salesRepEmail: request.salesRepEmail || '',
              salesRepCompany: request.salesRepCompany || 'Company Not Specified',
              products: request.products || ['Product information not provided'],
              isUrgent: isRequestUrgent(request)
            }));
            setMeetingRequests(enrichedRequests);
          }

          // Load approved reps
          loadApprovedReps(currentUser.id);
        }
        
        // Note: External invitations feature temporarily disabled for performance
        // if (user.userType === 'sales') {
        //   const invitations = ExternalProviderInvitation.getInvitationsBySalesRep(user.id);
        //   setExternalInvitations(invitations);
        //   
        //   const inviteStats = ExternalProviderInvitation.getInvitationStats(user.id);
        //   setInvitationStats(inviteStats);
        // }

        // Load pre-approval count for sales reps and managers
        if (userType === 'sales' || userType === 'manager') {
          loadPreApprovalCount(currentUser.id);
        }

        // Check for contextual help needs
        checkForContextualHelp(currentUser);
      }
      setLoading(false);
    };

    const checkForContextualHelp = (user) => {
      if (!user) return;

      const helpNeeded = ContextualHelp.shouldShowHelp(user.id, 'dashboardVisit');
      if (helpNeeded) {
        if (helpNeeded.type === 'tour') {
          setShowWelcomeBanner(true);
        }
        setHelpContext(helpNeeded.reason);
      }

      // Check for multiple pending requests
      if (meetingRequests.length > 2) {
        const bulkHelp = ContextualHelp.shouldShowHelp(user.id, 'multiplePendingRequests');
        if (bulkHelp) {
          setHelpContext('multiplePendingRequests');
        }
      }
    };

    loadData();
  }, [effectiveUser, effectiveUser?.id]); // Re-run when user changes or we navigate back

  const loadApprovedReps = (userId) => {
    try {
      const approvedRepsData = localStorage.getItem(`approved_reps_${userId}`);
      if (approvedRepsData) {
        setApprovedReps(JSON.parse(approvedRepsData));
      }
    } catch (error) {
      logger.error('Error loading approved reps', { error: error.message });
    }
  };

  const loadPreApprovalCount = async (salesRepId) => {
    try {
      const customers = await preApprovedRepsService.getCustomersWhoPreApproved(salesRepId);
      setPreApprovalCount(customers.length);
    } catch (error) {
      logger.error('Error loading pre-approval count', { error: error.message });
      setPreApprovalCount(0);
    }
  };

  const isRequestUrgent = (request) => {
    const requestDate = new Date(request.date);
    const now = new Date();
    const hoursUntilMeeting = (requestDate - now) / (1000 * 60 * 60);
    return hoursUntilMeeting <= 48; // Urgent if within 48 hours
  };

  const isRepApproved = (repEmail, repName) => {
    return approvedReps.some(rep => 
      rep.email === repEmail || 
      (rep.name === repName && repName !== 'Unknown Sales Rep')
    );
  };

  const handleQuickApprove = async (requestId, request) => {
    setActionInProgress(requestId);
    try {
      const updatedMeeting = MeetingStorage.approveMeetingRequest(requestId, {
        confirmedBy: currentUser.id,
        confirmedAt: new Date().toISOString()
      });

      if (updatedMeeting) {
        setActionStatus('Meeting request approved successfully!');
        // Update notification count
        if (updateNotificationCount) {
          updateNotificationCount();
        }
        // Refresh data
        const requests = MeetingStorage.getMeetingRequestsForProvider(currentUser.id);
        const enrichedRequests = requests.map(req => ({
          ...req,
          requestId: req.id,
          salesRepName: req.salesRepName || req.salesRep || 'Unknown Sales Rep',
          salesRepEmail: req.salesRepEmail || '',
          salesRepCompany: req.salesRepCompany || 'Company Not Specified',
          products: req.products || ['Product information not provided'],
          isUrgent: isRequestUrgent(req)
        }));
        setMeetingRequests(enrichedRequests);

        // Refresh meetings
        const userMeetings = MeetingStorage.getMeetingsForUser(effectiveUser.id, effectiveUser.userType);
        setMeetings(userMeetings);
        const stats = MeetingStorage.getMeetingStats(effectiveUser.id, effectiveUser.userType);
        setMeetingStats(stats);
        
        setTimeout(() => setActionStatus(''), 3000);
      } else {
        throw new Error('Failed to approve meeting');
      }
    } catch (error) {
      logger.error('Error approving request', { error: error.message });
      setActionStatus('Error approving meeting request. Please try again.');
      setTimeout(() => setActionStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleQuickDecline = async (requestId, repName) => {
    setActionInProgress(requestId);
    try {
      const updatedMeeting = MeetingStorage.declineMeetingRequest(requestId, {
        declinedBy: currentUser.id,
        reason: 'Request declined by healthcare provider'
      });

      if (updatedMeeting) {
        setActionStatus(`Meeting request from ${repName} declined.`);
        // Update notification count
        if (updateNotificationCount) {
          updateNotificationCount();
        }
        // Refresh data
        const requests = MeetingStorage.getMeetingRequestsForProvider(currentUser.id);
        const enrichedRequests = requests.map(req => ({
          ...req,
          requestId: req.id,
          salesRepName: req.salesRepName || req.salesRep || 'Unknown Sales Rep',
          salesRepEmail: req.salesRepEmail || '',
          salesRepCompany: req.salesRepCompany || 'Company Not Specified',
          products: req.products || ['Product information not provided'],
          isUrgent: isRequestUrgent(req)
        }));
        setMeetingRequests(enrichedRequests);

        // Refresh meetings stats
        const stats = MeetingStorage.getMeetingStats(effectiveUser.id, effectiveUser.userType);
        setMeetingStats(stats);
        
        setTimeout(() => setActionStatus(''), 3000);
      } else {
        throw new Error('Failed to decline meeting');
      }
    } catch (error) {
      logger.error('Error declining request', { error: error.message });
      setActionStatus('Error declining meeting request. Please try again.');
      setTimeout(() => setActionStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  // Bulk action handlers
  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => {
      const newSelected = prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId];
      setShowBulkActions(newSelected.length > 0);
      return newSelected;
    });
  };

  const handleSelectAllRequests = () => {
    const allRequestIds = meetingRequests.map(req => req.requestId);
    const allSelected = selectedRequests.length === meetingRequests.length;
    
    if (allSelected) {
      setSelectedRequests([]);
      setShowBulkActions(false);
    } else {
      setSelectedRequests(allRequestIds);
      setShowBulkActions(true);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) return;
    
    setBulkActionInProgress(true);
    let successCount = 0;
    let errorCount = 0;

    for (const requestId of selectedRequests) {
      try {
        const request = meetingRequests.find(req => req.requestId === requestId);
        const updatedMeeting = MeetingStorage.approveMeetingRequest(requestId, {
          confirmedBy: currentUser.id,
          confirmedAt: new Date().toISOString()
        });
        if (updatedMeeting) successCount++;
        else errorCount++;
      } catch (error) {
        logger.error('Error approving request', { requestId, error: error.message });
        errorCount++;
      }
    }

    // Refresh data
    const requests = MeetingStorage.getMeetingRequestsForProvider(currentUser.id);
    const enrichedRequests = requests.map(req => ({
      ...req,
      requestId: req.id,
      salesRepName: req.salesRepName || req.salesRep || 'Unknown Sales Rep',
      salesRepEmail: req.salesRepEmail || '',
      salesRepCompany: req.salesRepCompany || 'Company Not Specified',
      products: req.products || ['Product information not provided'],
      isUrgent: isRequestUrgent(req)
    }));
    setMeetingRequests(enrichedRequests);

    // Refresh meetings and stats
    const userMeetings = MeetingStorage.getMeetingsForUser(effectiveUser.id, effectiveUser.userType);
    setMeetings(userMeetings);
    const stats = MeetingStorage.getMeetingStats(effectiveUser.id, effectiveUser.userType);
    setMeetingStats(stats);

    // Clear selections and show status
    setSelectedRequests([]);
    setShowBulkActions(false);
    setBulkActionInProgress(false);

    if (errorCount === 0) {
      setActionStatus(`Successfully approved ${successCount} meeting request${successCount > 1 ? 's' : ''}!`);
    } else {
      setActionStatus(`Approved ${successCount} requests. ${errorCount} failed. Please try again for failed requests.`);
    }

    // Update notification count after bulk actions
    if (updateNotificationCount && successCount > 0) {
      updateNotificationCount();
    }

    setTimeout(() => setActionStatus(''), 4000);
  };

  const handleBulkDecline = async () => {
    if (selectedRequests.length === 0) return;
    
    setBulkActionInProgress(true);
    let successCount = 0;
    let errorCount = 0;

    for (const requestId of selectedRequests) {
      try {
        const updatedMeeting = MeetingStorage.declineMeetingRequest(requestId, {
          declinedBy: currentUser.id,
          reason: 'Request declined by healthcare provider - bulk action'
        });
        if (updatedMeeting) successCount++;
        else errorCount++;
      } catch (error) {
        logger.error('Error declining request', { requestId, error: error.message });
        errorCount++;
      }
    }

    // Refresh data
    const requests = MeetingStorage.getMeetingRequestsForProvider(currentUser.id);
    const enrichedRequests = requests.map(req => ({
      ...req,
      requestId: req.id,
      salesRepName: req.salesRepName || req.salesRep || 'Unknown Sales Rep',
      salesRepEmail: req.salesRepEmail || '',
      salesRepCompany: req.salesRepCompany || 'Company Not Specified',
      products: req.products || ['Product information not provided'],
      isUrgent: isRequestUrgent(req)
    }));
    setMeetingRequests(enrichedRequests);

    // Refresh meetings stats
    const stats = MeetingStorage.getMeetingStats(effectiveUser.id, effectiveUser.userType);
    setMeetingStats(stats);

    // Clear selections and show status
    setSelectedRequests([]);
    setShowBulkActions(false);
    setBulkActionInProgress(false);

    if (errorCount === 0) {
      setActionStatus(`Successfully declined ${successCount} meeting request${successCount > 1 ? 's' : ''}!`);
    } else {
      setActionStatus(`Declined ${successCount} requests. ${errorCount} failed. Please try again for failed requests.`);
    }

    // Update notification count after bulk actions
    if (updateNotificationCount && successCount > 0) {
      updateNotificationCount();
    }

    setTimeout(() => setActionStatus(''), 4000);
  };

  // Help handlers
  const handleHelpAction = (actionType, tip) => {
    switch (actionType) {
      case 'startTour':
        setShowTour(true);
        setShowWelcomeBanner(false);
        break;
      case 'highlightBulkActions':
        // Scroll to bulk actions section
        const bulkSection = document.querySelector('[data-tour="bulk-actions"]');
        if (bulkSection) {
          bulkSection.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      case 'highlightCustomization':
        // Highlight customization button
        const customizeBtn = document.querySelector('[data-tour="customize-button"]');
        if (customizeBtn) {
          customizeBtn.scrollIntoView({ behavior: 'smooth' });
          customizeBtn.classList.add('ring-4', 'ring-blue-300');
          setTimeout(() => customizeBtn.classList.remove('ring-4', 'ring-blue-300'), 3000);
        }
        break;
      case 'dismiss':
        setHelpContext(null);
        break;
    }
  };

  const handleTourComplete = () => {
    setShowTour(false);
    if (currentUser) {
      ContextualHelp.updateHelpHistory(currentUser.id, { 
        tourCompleted: true,
        tourCompletedAt: new Date().toISOString()
      });
    }
  };

  const handleTourSkip = () => {
    setShowTour(false);
    setShowWelcomeBanner(false);
  };

  // Get current date with UTC timezone for consistent comparison
  const now = new Date();
  const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  currentDate.setHours(0, 0, 0, 0);
  
  const startOfWeek = new Date(currentDate);
  const day = startOfWeek.getDay();
  const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
  startOfWeek.setDate(diff);
  startOfWeek.setHours(0, 0, 0, 0);
  
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 4);
  endOfWeek.setHours(23, 59, 59, 999);
  
  // Helper function to parse date strings with UTC timezone handling (consistent with other components)
  const parseLocalDate = (dateString) => {
    if (!dateString) return new Date();

    // Handle YYYY-MM-DD format with UTC timezone to avoid date shifts
    if (dateString && dateString.includes('-') && dateString.split('-').length === 3) {
      // Use UTC parsing to ensure consistent date handling
      const utcDate = new Date(dateString + 'T12:00:00Z');

      return utcDate;
    } else {
      // For other date formats, try to parse normally but log for debugging
      const parsed = new Date(dateString);

      return parsed;
    }
  };

  const meetingsThisWeekCount = meetings.filter(meeting => {
    const meetingDate = parseLocalDate(meeting.date);
    meetingDate.setHours(0, 0, 0, 0);
    return meetingDate >= startOfWeek && meetingDate <= endOfWeek;
  }).length;

  // Get today's meetings with improved date parsing
  const todaysMeetings = meetings.filter(meeting => {
    try {
      const dateValue = meeting.date || meeting.meeting_date;

      if (!dateValue) {
        logger.warn('No date field found in meeting', { meetingId: meeting.id });
        return false;
      }

      const meetingDate = parseLocalDate(dateValue);

      // Validate the parsed date
      if (isNaN(meetingDate.getTime())) {
        logger.warn('Invalid date in meeting', {
          id: meeting.id,
          dateField: meeting.date || meeting.meeting_date,
          parsedDate: meetingDate
        });
        return false;
      }

      meetingDate.setHours(0, 0, 0, 0);
      const isToday = meetingDate.getTime() === currentDate.getTime();

      // Check status - handle both lowercase and capitalized versions
      const status = meeting.status ? meeting.status.toLowerCase() : '';
      const validStatus = (status === 'confirmed' || status === 'pending') && status !== 'cancelled';

      // Debug logging for today's meetings only
      if (isToday) {
        logger.debug('📅 DEBUG: Today meeting found:', {
          id: meeting.id,
          originalDate: meeting.date || meeting.meeting_date,
          parsedDate: meetingDate.toISOString(),
          time: meeting.time || meeting.start_time,
          status: meeting.status,
          normalizedStatus: status,
          validStatus,
          isToday
        });
      }

      return isToday && validStatus;
    } catch (error) {
      logger.error('Error parsing meeting date', { error: error.message, meetingId: meeting.id });
      return false;
    }
  }).sort((a, b) => {
    try {
      const timeA = (a.time || a.start_time || '12:00').split(':').map(Number);
      const timeB = (b.time || b.start_time || '12:00').split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    } catch (error) {
      logger.error('Error sorting meetings by time', { error: error.message });
      return 0;
    }
  });

  // Debug summary with timezone information
  logger.debug('📊 DEBUG: Meeting Summary:', {
    totalMeetings: meetings.length,
    todayMeetings: todaysMeetings.length,
    todaysDate: new Date().toDateString(),
    todaysDateISO: currentDate.toISOString(),
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    upcomingMeetingDates: meetings
      .filter(m => m.date || m.meeting_date)
      .map(m => ({
        original: m.date || m.meeting_date,
        parsed: (() => {
          const dateStr = m.date || m.meeting_date;
          if (dateStr.includes('T')) return new Date(dateStr).toDateString();
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day).toDateString();
        })()
      }))
      .slice(0, 5)
  });

  // Calculate end of this week for comprehensive upcoming meetings view
  const endOfThisWeek = new Date(endOfWeek);
  endOfThisWeek.setHours(23, 59, 59, 999);

  let filteredUpcomingMeetings = [];
  let comprehensiveUpcomingMeetings = [];

  if (userType === 'sales') {
    // For sales: show next upcoming meetings (using same logic as MeetingsView.js)
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    filteredUpcomingMeetings = meetings.filter(m => {
      if (!m.date || m.status === 'cancelled' || m.status === 'declined') return false;

      const meetingDate = parseLocalDate(m.date);
      meetingDate.setHours(0, 0, 0, 0);

      // Simple comparison: include today and future meetings (same logic as MeetingTrendChart)
      return meetingDate >= todayStart;
    })
    // Sort by date/time to show truly next meetings first
    .sort((a, b) => {
      const dateA = parseLocalDate(a.date);
      const dateB = parseLocalDate(b.date);
      if (dateA.getTime() === dateB.getTime()) {
        // If same date, sort by time
        const timeA = a.time || '12:00 PM';
        const timeB = b.time || '12:00 PM';
        return timeA.localeCompare(timeB);
      }
      return dateA - dateB;
    });

    comprehensiveUpcomingMeetings = filteredUpcomingMeetings;
  } else {
    // For physicians: show next 3 upcoming meetings regardless of date
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    filteredUpcomingMeetings = meetings.filter(m => {
      if (!m.date || m.status === 'cancelled') return false;

      const meetingDate = parseLocalDate(m.date);
      meetingDate.setHours(0, 0, 0, 0);

      // Include all future meetings (confirmed or pending), regardless of how far out
      return meetingDate >= todayStart &&
             (m.status === 'confirmed' || m.status === 'pending');
    })
    // Sort by date/time
    .sort((a, b) => {
      const dateA = parseLocalDate(a.date);
      const dateB = parseLocalDate(b.date);
      if (dateA.getTime() === dateB.getTime()) {
        const timeA = a.time || '12:00 PM';
        const timeB = b.time || '12:00 PM';
        return timeA.localeCompare(timeB);
      }
      return dateA - dateB;
    });

    // Comprehensive view: all meetings beyond this week
    comprehensiveUpcomingMeetings = meetings.filter(m => {
      const meetingDate = parseLocalDate(m.date);
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate > endOfThisWeek &&
             (m.status === 'confirmed' || m.status === 'pending') &&
             m.status !== 'cancelled';
    });
  }

  const handleMeetingClick = (meeting) => {
    setSelectedMeetingId(meeting.id);
    setCurrentView('meeting-detail');
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <MobileLoadingSpinner 
          message="Loading your dashboard..." 
          size="large"
        />
      </div>
    );
  }
  
  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto overflow-x-hidden" data-tour="welcome">
      {/* Delegation Banner for Office Staff */}
      {isDelegated && delegatingFor && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-800">
                Managing meetings for {delegatingFor.name || delegatingFor.fullName}
              </p>
              <p className="text-xs text-blue-600">
                You have permission to handle meeting requests and manage availability
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contextual Help Tour */}
      <GuidedTour
        isActive={showTour}
        userType={userType}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
        autoStart={true}
      />

      {/* Welcome Help Banner */}
      {showWelcomeBanner && currentUser && (
        <div className="mb-6">
          <HelpBanner
            userId={currentUser.id}
            context="firstLogin"
            onAction={handleHelpAction}
            onDismiss={() => setShowWelcomeBanner(false)}
          />
        </div>
      )}

      {/* Contextual Help Banner */}
      {helpContext && currentUser && helpContext !== 'firstLogin' && (
        <div className="mb-6">
          <HelpBanner
            userId={currentUser.id}
            context={helpContext}
            onAction={handleHelpAction}
            onDismiss={() => setHelpContext(null)}
          />
        </div>
      )}

      {/* Header with Customization Controls */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              Welcome back{
                currentUser?.first_name ? 
                  `, ${currentUser.first_name} ${currentUser.last_name || ''}`.trim() :
                  profileData.name ? 
                    `, ${profileData.name}` : ''
              }
            </h2>
            <p className="text-gray-600">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          
          {/* Customization Controls - Disabled for customers */}
          {/* Removed dashboard rearrangement feature for customers */}
        </div>
        
        {/* Customization Instructions - Removed for customers */}
      </div>

      {/* Action Status */}
      {actionStatus && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          actionStatus.includes('Error') 
            ? 'bg-red-50 border border-red-200 text-red-700' 
            : 'bg-green-50 border border-green-200 text-green-700'
        }`}>
          {actionStatus.includes('Error') ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <CheckCircle className="w-5 h-5" />
          )}
          <span>{actionStatus}</span>
        </div>
      )}
      
      {/* Subscription Warning for Sales Reps and Managers */}
      {currentUser && PaymentUtils.isPaymentRequired(currentUser.userType) && !hasValidSubscription && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-orange-800 mb-2">Subscription Required</h3>
              <p className="text-orange-700 text-sm mb-3">
                Your account requires an active subscription to schedule meetings and access premium features. 
                Upgrade now to unlock the full potential of RepConnect.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setCurrentView('settings')}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Subscribe Now</span>
                </button>
                <span className="text-orange-600 text-sm font-medium">
                  {PaymentUtils.formatCurrency(PaymentUtils.getPlanForUserType(currentUser.userType)?.price || 0)}/month
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Schedule Section - For all users */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-blue-600" />
            Today's Schedule
          </h3>
          {todaysMeetings.length > 0 && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {todaysMeetings.length} meeting{todaysMeetings.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          {todaysMeetings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {todaysMeetings.map(meeting => (
                <EnhancedMeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onClick={() => handleMeetingClick(meeting)}
                  userType={userType}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 font-medium">No meetings scheduled for today</p>
              <p className="text-sm text-gray-500 mt-0.5">Enjoy your free day!</p>
            </div>
          )}
        </div>
      </div>

      {/* Removed Meeting Requests Section - users access via Quick Actions button or Meetings tab */}

      {/* Customizable Dashboard Sections */}
      {renderSections()}
    </div>
  );
};

export default React.memo(Dashboard);
